/**
 * Chess Piece Movement Logic
 * 
 * Encapsulates movement rules for each piece type.
 * Implements requirements 2.1-2.8 from the chess-game specification.
 */

import {
  Position,
  ChessPiece,
  Board,
  PlayerColor,
  Move,
  isValidPosition,
  positionsEqual
} from './types';

/**
 * ChessPieceLogic class handles movement rules for all piece types
 */
export class ChessPieceLogic {
  /**
   * Gets all legal moves for a king from a given position
   * Requirement 2.1: King moves one square in any direction
   * 
   * @param position Current position of the king
   * @param board Current board state
   * @returns Array of legal destination positions
   */
  public getKingMoves(position: Position, board: Board): Position[] {
    const moves: Position[] = [];
    const piece = board[position.rank][position.file];
    
    if (!piece || piece.type !== 'king') {
      return moves;
    }
    
    // King can move one square in any direction (8 possible moves)
    const directions = [
      { file: -1, rank: -1 }, // Down-left
      { file: 0, rank: -1 },  // Down
      { file: 1, rank: -1 },  // Down-right
      { file: -1, rank: 0 },  // Left
      { file: 1, rank: 0 },   // Right
      { file: -1, rank: 1 },  // Up-left
      { file: 0, rank: 1 },   // Up
      { file: 1, rank: 1 }    // Up-right
    ];
    
    for (const dir of directions) {
      const newPos: Position = {
        file: position.file + dir.file,
        rank: position.rank + dir.rank
      };
      
      if (isValidPosition(newPos)) {
        const targetPiece = board[newPos.rank][newPos.file];
        // Can move to empty square or capture opponent's piece
        if (!targetPiece || targetPiece.color !== piece.color) {
          moves.push(newPos);
        }
      }
    }
    
    return moves;
  }

  /**
   * Gets all legal moves for a queen from a given position
   * Requirement 2.2: Queen moves any distance along ranks, files, or diagonals
   * 
   * @param position Current position of the queen
   * @param board Current board state
   * @returns Array of legal destination positions
   */
  public getQueenMoves(position: Position, board: Board): Position[] {
    const piece = board[position.rank][position.file];
    
    if (!piece || piece.type !== 'queen') {
      return [];
    }
    
    // Queen combines rook and bishop moves
    return [
      ...this.getRookMoves(position, board),
      ...this.getBishopMoves(position, board)
    ];
  }

  /**
   * Gets all legal moves for a rook from a given position
   * Requirement 2.3: Rook moves any distance along ranks or files
   * 
   * @param position Current position of the rook
   * @param board Current board state
   * @returns Array of legal destination positions
   */
  public getRookMoves(position: Position, board: Board): Position[] {
    const moves: Position[] = [];
    const piece = board[position.rank][position.file];
    
    if (!piece || (piece.type !== 'rook' && piece.type !== 'queen')) {
      return moves;
    }
    
    // Rook moves along ranks and files (4 directions)
    const directions = [
      { file: 0, rank: 1 },   // Up
      { file: 0, rank: -1 },  // Down
      { file: 1, rank: 0 },   // Right
      { file: -1, rank: 0 }   // Left
    ];
    
    for (const dir of directions) {
      let distance = 1;
      while (true) {
        const newPos: Position = {
          file: position.file + (dir.file * distance),
          rank: position.rank + (dir.rank * distance)
        };
        
        if (!isValidPosition(newPos)) {
          break; // Out of bounds
        }
        
        const targetPiece = board[newPos.rank][newPos.file];
        
        if (!targetPiece) {
          // Empty square - can move here
          moves.push(newPos);
          distance++;
        } else if (targetPiece.color !== piece.color) {
          // Opponent's piece - can capture
          moves.push(newPos);
          break; // Can't move further
        } else {
          // Own piece - blocked
          break;
        }
      }
    }
    
    return moves;
  }

  /**
   * Gets all legal moves for a bishop from a given position
   * Requirement 2.4: Bishop moves any distance along diagonals
   * 
   * @param position Current position of the bishop
   * @param board Current board state
   * @returns Array of legal destination positions
   */
  public getBishopMoves(position: Position, board: Board): Position[] {
    const moves: Position[] = [];
    const piece = board[position.rank][position.file];
    
    if (!piece || (piece.type !== 'bishop' && piece.type !== 'queen')) {
      return moves;
    }
    
    // Bishop moves along diagonals (4 directions)
    const directions = [
      { file: 1, rank: 1 },    // Up-right
      { file: 1, rank: -1 },   // Down-right
      { file: -1, rank: 1 },   // Up-left
      { file: -1, rank: -1 }   // Down-left
    ];
    
    for (const dir of directions) {
      let distance = 1;
      while (true) {
        const newPos: Position = {
          file: position.file + (dir.file * distance),
          rank: position.rank + (dir.rank * distance)
        };
        
        if (!isValidPosition(newPos)) {
          break; // Out of bounds
        }
        
        const targetPiece = board[newPos.rank][newPos.file];
        
        if (!targetPiece) {
          // Empty square - can move here
          moves.push(newPos);
          distance++;
        } else if (targetPiece.color !== piece.color) {
          // Opponent's piece - can capture
          moves.push(newPos);
          break; // Can't move further
        } else {
          // Own piece - blocked
          break;
        }
      }
    }
    
    return moves;
  }

  /**
   * Gets all legal moves for a knight from a given position
   * Requirement 2.5: Knight moves in L-shape (2 squares + 1 square perpendicular)
   * 
   * @param position Current position of the knight
   * @param board Current board state
   * @returns Array of legal destination positions
   */
  public getKnightMoves(position: Position, board: Board): Position[] {
    const moves: Position[] = [];
    const piece = board[position.rank][position.file];
    
    if (!piece || piece.type !== 'knight') {
      return moves;
    }
    
    // Knight has 8 possible L-shaped moves
    const knightMoves = [
      { file: 2, rank: 1 },   // 2 right, 1 up
      { file: 2, rank: -1 },  // 2 right, 1 down
      { file: -2, rank: 1 },  // 2 left, 1 up
      { file: -2, rank: -1 }, // 2 left, 1 down
      { file: 1, rank: 2 },   // 1 right, 2 up
      { file: 1, rank: -2 },  // 1 right, 2 down
      { file: -1, rank: 2 },  // 1 left, 2 up
      { file: -1, rank: -2 }  // 1 left, 2 down
    ];
    
    for (const move of knightMoves) {
      const newPos: Position = {
        file: position.file + move.file,
        rank: position.rank + move.rank
      };
      
      if (isValidPosition(newPos)) {
        const targetPiece = board[newPos.rank][newPos.file];
        // Can move to empty square or capture opponent's piece
        if (!targetPiece || targetPiece.color !== piece.color) {
          moves.push(newPos);
        }
      }
    }
    
    return moves;
  }

  /**
   * Gets all legal moves for a pawn from a given position
   * Requirement 2.6: Pawn moves forward one square, two from start, diagonal captures
   * 
   * @param position Current position of the pawn
   * @param board Current board state
   * @param lastMove Optional last move for en passant detection
   * @returns Array of legal destination positions
   */
  public getPawnMoves(position: Position, board: Board, lastMove?: Move): Position[] {
    const moves: Position[] = [];
    const piece = board[position.rank][position.file];
    
    if (!piece || piece.type !== 'pawn') {
      return moves;
    }
    
    // Pawns move differently based on color
    const direction = piece.color === 'white' ? 1 : -1;
    const startRank = piece.color === 'white' ? 1 : 6;
    
    // Forward move (one square)
    const forwardOne: Position = {
      file: position.file,
      rank: position.rank + direction
    };
    
    if (isValidPosition(forwardOne) && !board[forwardOne.rank][forwardOne.file]) {
      moves.push(forwardOne);
      
      // Forward move (two squares from starting position)
      if (position.rank === startRank) {
        const forwardTwo: Position = {
          file: position.file,
          rank: position.rank + (direction * 2)
        };
        
        if (isValidPosition(forwardTwo) && !board[forwardTwo.rank][forwardTwo.file]) {
          moves.push(forwardTwo);
        }
      }
    }
    
    // Diagonal captures
    const captureLeft: Position = {
      file: position.file - 1,
      rank: position.rank + direction
    };
    
    const captureRight: Position = {
      file: position.file + 1,
      rank: position.rank + direction
    };
    
    // Check left diagonal capture
    if (isValidPosition(captureLeft)) {
      const targetPiece = board[captureLeft.rank][captureLeft.file];
      if (targetPiece && targetPiece.color !== piece.color) {
        moves.push(captureLeft);
      }
      
      // Check for en passant on left
      if (this.canEnPassant(position, captureLeft, board, lastMove)) {
        moves.push(captureLeft);
      }
    }
    
    // Check right diagonal capture
    if (isValidPosition(captureRight)) {
      const targetPiece = board[captureRight.rank][captureRight.file];
      if (targetPiece && targetPiece.color !== piece.color) {
        moves.push(captureRight);
      }
      
      // Check for en passant on right
      if (this.canEnPassant(position, captureRight, board, lastMove)) {
        moves.push(captureRight);
      }
    }
    
    return moves;
  }

  /**
   * Checks if a path between two positions is clear (no pieces blocking)
   * Requirement 2.7: Pieces cannot move through other pieces (except knights)
   * 
   * @param from Starting position
   * @param to Ending position
   * @param board Current board state
   * @returns True if path is clear, false otherwise
   */
  public isPathClear(from: Position, to: Position, board: Board): boolean {
    // Calculate direction
    const fileDir = Math.sign(to.file - from.file);
    const rankDir = Math.sign(to.rank - from.rank);
    
    // If not moving in a straight line or diagonal, path check doesn't apply
    if (fileDir !== 0 && rankDir !== 0 && Math.abs(to.file - from.file) !== Math.abs(to.rank - from.rank)) {
      return false;
    }
    
    // Check each square along the path (excluding start and end)
    let currentFile = from.file + fileDir;
    let currentRank = from.rank + rankDir;
    
    while (currentFile !== to.file || currentRank !== to.rank) {
      if (board[currentRank][currentFile] !== null) {
        return false; // Path is blocked
      }
      
      currentFile += fileDir;
      currentRank += rankDir;
    }
    
    return true;
  }

  /**
   * Checks if a square is under attack by a specific player
   * 
   * @param position Position to check
   * @param byPlayer Player color to check attacks from
   * @param board Current board state
   * @returns True if square is attacked, false otherwise
   */
  public isSquareAttacked(position: Position, byPlayer: PlayerColor, board: Board): boolean {
    // Check all pieces of the attacking player
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        
        if (piece && piece.color === byPlayer) {
          const piecePos: Position = { file, rank };
          const moves = this.getPieceMoves(piecePos, board);
          
          // Check if any move targets the position
          if (moves.some(move => positionsEqual(move, position))) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  /**
   * Gets all squares attacked by a specific player
   * 
   * @param player Player color
   * @param board Current board state
   * @returns Array of attacked positions
   */
  public getAllAttackedSquares(player: PlayerColor, board: Board): Position[] {
    const attacked: Position[] = [];
    const seen = new Set<string>();
    
    // Check all pieces of the player
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        
        if (piece && piece.color === player) {
          const piecePos: Position = { file, rank };
          const moves = this.getPieceMoves(piecePos, board);
          
          for (const move of moves) {
            const key = `${move.file},${move.rank}`;
            if (!seen.has(key)) {
              attacked.push(move);
              seen.add(key);
            }
          }
        }
      }
    }
    
    return attacked;
  }

  /**
   * Gets all legal moves for a piece at a given position
   * 
   * @param position Position of the piece
   * @param board Current board state
   * @param lastMove Optional last move for en passant
   * @returns Array of legal destination positions
   */
  public getPieceMoves(position: Position, board: Board, lastMove?: Move): Position[] {
    const piece = board[position.rank][position.file];
    
    if (!piece) {
      return [];
    }
    
    switch (piece.type) {
      case 'king':
        return this.getKingMoves(position, board);
      case 'queen':
        return this.getQueenMoves(position, board);
      case 'rook':
        return this.getRookMoves(position, board);
      case 'bishop':
        return this.getBishopMoves(position, board);
      case 'knight':
        return this.getKnightMoves(position, board);
      case 'pawn':
        return this.getPawnMoves(position, board, lastMove);
      default:
        return [];
    }
  }

  /**
   * Checks if en passant capture is possible
   * 
   * @param from Pawn position
   * @param to Target capture position
   * @param board Current board state
   * @param lastMove Last move made
   * @returns True if en passant is legal
   */
  private canEnPassant(from: Position, to: Position, board: Board, lastMove?: Move): boolean {
    if (!lastMove) {
      return false;
    }
    
    const piece = board[from.rank][from.file];
    if (!piece || piece.type !== 'pawn') {
      return false;
    }
    
    // Check if last move was a pawn moving two squares
    if (lastMove.piece.type !== 'pawn') {
      return false;
    }
    
    const moveDistance = Math.abs(lastMove.to.rank - lastMove.from.rank);
    if (moveDistance !== 2) {
      return false;
    }
    
    // Check if the opponent's pawn is beside our pawn
    const opponentPawnPos = lastMove.to;
    if (opponentPawnPos.rank !== from.rank) {
      return false;
    }
    
    if (Math.abs(opponentPawnPos.file - from.file) !== 1) {
      return false;
    }
    
    // Check if target square is behind the opponent's pawn
    const direction = piece.color === 'white' ? 1 : -1;
    const expectedTarget: Position = {
      file: opponentPawnPos.file,
      rank: from.rank + direction
    };
    
    return positionsEqual(to, expectedTarget);
  }
}
